import './style.css';

import { route, startRouter } from './router.js';
import { renderNav } from './components/nav.js';
import { initAuth, onAuthChange } from './lib/auth.js';
import { renderAuthButton } from './components/auth-modal.js';
import { scheduleAutosave, setLibraryChangedCallback } from './lib/storage.js';
import { isSupabaseConfigured } from './lib/supabase.js';
import { renderStatusIndicator } from './components/status-indicator.js';
import { initExplore } from './explore/explore.js';
import { initViewer, cleanupViewer } from './viewer/viewer.js';
import { initMyAnimations, cleanupMyAnimations } from './my-animations/my-animations.js';
import { state, loadPrefs, loadProject, getSelectedKeyframe, undo, redo, pushUndo, notify, renderUndoIndicator, setCloudSaveHook, snapshotKf, recordKfHistory, setHistoryConfirmHook, setHistoryChangeHook } from './editor/state.js';
import { createClawdDefaults } from './editor/defaults.js';
import { renderKeyframeStrip } from './editor/keyframes.js';
import { renderGrid, renderGridControls, renderGridProps } from './editor/grid.js';
import { renderUnicodePanel, addRecent } from './editor/unicode.js';
import { renderPalettesPanel } from './editor/palettes.js';
import { renderSequenceManager, renderTimeline, renderTimelinePreview, renderTimelineControls, renderTimelineExport, togglePlayback, stopPlayback } from './editor/timeline.js';
import { setupImportExport } from './editor/export.js';
import { setupTabSwitching, closeModal } from './editor/ui.js';
import { registerRenderAll } from './editor/render-bridge.js';
import { initHistoryPanel, renderHistoryPanel } from './editor/history-panel.js';

// ================================================================
// EDITOR: RENDER ALL
// ================================================================
let editorInitialized = false;

function renderAll() {
  renderKeyframeStrip();
  renderGrid();
  renderGridProps();
  renderGridControls();
  renderUnicodePanel();
  renderPalettesPanel();
  renderSequenceManager();
  renderTimeline();
  renderTimelineControls();
  renderTimelineExport();
  renderUndoIndicator();
  renderHistoryPanel();
}

registerRenderAll(renderAll);

function initEditor() {
  if (editorInitialized) {
    renderAll();
    return;
  }

  loadPrefs();

  if (!loadProject()) {
    state.project = createClawdDefaults();
  }

  const ids = Object.keys(state.project.keyframes);
  state.ui.selectedKeyframeId = ids[0] || null;

  if (!state.project.sequences || state.project.sequences.length === 0) {
    state.project.sequences = [{ name: "animation", steps: [], loop: true }];
  }

  renderAll();

  setupTabSwitching(() => {
    renderSequenceManager();
    renderTimeline();
    renderTimelinePreview();
    renderTimelineControls();
  });

  setupKeyboardShortcuts();
  setupImportExport();
  setupCloudSave();
  initHistoryPanel();
  editorInitialized = true;
}

function setupCloudSave() {
  // Hook autosave into the debounced save cycle
  setCloudSaveHook(() => {
    scheduleAutosave();
  });

  // When cloud hydration merges new data, refresh the editor UI
  setLibraryChangedCallback(() => {
    if (document.getElementById('page-editor')?.classList.contains('active')) {
      renderAll();
    }
  });

  // Status indicator in the topbar
  const topbar = document.querySelector('#page-editor .topbar');
  if (!topbar) return;
  let statusEl = document.getElementById('save-status');
  if (!statusEl) {
    statusEl = document.createElement('span');
    statusEl.id = 'save-status';
    statusEl.className = 'save-status';
    topbar.appendChild(statusEl);
    renderStatusIndicator();
  }

  // Re-render the sequence manager when auth state changes so that
  // Save/Publish buttons enable/disable correctly.
  onAuthChange(() => {
    if (document.getElementById('page-editor')?.classList.contains('active')) {
      renderSequenceManager();
    }
  });
}

// ================================================================
// EXPLORE PAGE: Placeholder with animated Clawd
// ================================================================
function initExplorePlaceholder() {
  const mascot = document.getElementById('explore-hero-mascot');
  if (!mascot || mascot.dataset.initialized) return;
  mascot.dataset.initialized = 'true';

  // Hardcoded Clawd poses for the hero animation
  const poses = {
    default:    " \u2590\u259B\u2588\u2588\u2588\u259C\u258C \n\u259D\u259C\u2588\u2588\u2588\u2588\u2588\u259B\u2598\n  \u2598\u2598 \u259D\u259D  ",
    lookLeft:   " \u2590\u259F\u2588\u2588\u2588\u259F\u258C \n\u259D\u259C\u2588\u2588\u2588\u2588\u2588\u259B\u2598\n  \u2598\u2598 \u259D\u259D  ",
    lookRight:  " \u2590\u2599\u2588\u2588\u2588\u2599\u258C \n\u259D\u259C\u2588\u2588\u2588\u2588\u2588\u259B\u2598\n  \u2598\u2598 \u259D\u259D  ",
    armsUp:     "\u2597\u259F\u259B\u2588\u2588\u2588\u259C\u2599\u2596\n \u259C\u2588\u2588\u2588\u2588\u2588\u259B \n  \u2598\u2598 \u259D\u259D  ",
  };
  const seq = [
    { pose: 'default', duration: 800 },
    { pose: 'lookLeft', duration: 500 },
    { pose: 'lookLeft', duration: 500 },
    { pose: 'default', duration: 300 },
    { pose: 'lookRight', duration: 500 },
    { pose: 'lookRight', duration: 500 },
    { pose: 'default', duration: 300 },
    { pose: 'armsUp', duration: 600 },
    { pose: 'armsUp', duration: 600 },
    { pose: 'default', duration: 400 },
  ];

  let step = 0;
  let timer = null;

  function animate() {
    const { pose, duration } = seq[step % seq.length];
    mascot.textContent = poses[pose];
    step++;
    timer = setTimeout(animate, duration);
  }

  animate();

  // Return cleanup function
  return () => { clearTimeout(timer); };
}

// ================================================================
// ROUTES
// ================================================================
route('/explore', () => {
  document.getElementById('page-explore').classList.add('active');
  initExplore();
});

route('/editor', () => {
  document.getElementById('page-editor').classList.add('active');
  initEditor();
  return () => { stopPlayback(); };
});

route('/editor/:id', (params) => {
  document.getElementById('page-editor').classList.add('active');
  initEditor();
  // TODO Phase 4: Load animation from Supabase by params.id
  return () => { stopPlayback(); };
});

route('/my-animations', () => {
  document.getElementById('page-my-animations').classList.add('active');
  initMyAnimations();
  return () => { cleanupMyAnimations(); };
});

route('/view/:id', (params) => {
  document.getElementById('page-viewer').classList.add('active');
  initViewer(params.id);
  return () => { cleanupViewer(); };
});

// ================================================================
// KEYBOARD SHORTCUTS (global)
// ================================================================
function setupKeyboardShortcuts() {
  document.addEventListener("keydown", (e) => {
    if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") {
      return;
    }

    // Only handle editor shortcuts when editor page is active
    if (!document.getElementById('page-editor').classList.contains('active')) return;

    const kf = getSelectedKeyframe();

    if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) {
      e.preventDefault();
      undo();
      renderAll();
      return;
    }
    if ((e.metaKey || e.ctrlKey) && (e.key === "y" || (e.key === "z" && e.shiftKey))) {
      e.preventDefault();
      redo();
      renderAll();
      return;
    }

    if (!kf || !state.ui.selectedCell) return;

    if (e.key === "ArrowUp") {
      e.preventDefault();
      state.ui.selectedCell.row = Math.max(0, state.ui.selectedCell.row - 1);
      renderGrid(); renderGridProps();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      state.ui.selectedCell.row = Math.min(kf.rows - 1, state.ui.selectedCell.row + 1);
      renderGrid(); renderGridProps();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      state.ui.selectedCell.col = Math.max(0, state.ui.selectedCell.col - 1);
      renderGrid(); renderGridProps();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      state.ui.selectedCell.col = Math.min(kf.cols - 1, state.ui.selectedCell.col + 1);
      renderGrid(); renderGridProps();
    }

    if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      pushUndo();
      const snap = snapshotKf(kf);
      kf.grid[state.ui.selectedCell.row][state.ui.selectedCell.col].char = "";
      kf.grid[state.ui.selectedCell.row][state.ui.selectedCell.col].color = null;
      notify("clearCell");
      recordKfHistory(kf, "paint stroke", snap);
      renderGrid(); renderGridProps();
      return;
    }

    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      pushUndo();
      const snap = snapshotKf(kf);
      kf.grid[state.ui.selectedCell.row][state.ui.selectedCell.col].char = e.key;
      addRecent(e.key);
      notify("typeCell");
      recordKfHistory(kf, "paint stroke", snap);
      let nc = state.ui.selectedCell.col + 1;
      if (nc >= kf.cols) {
        nc = 0;
        state.ui.selectedCell.row = Math.min(kf.rows - 1, state.ui.selectedCell.row + 1);
      }
      state.ui.selectedCell.col = nc;
      renderGrid(); renderGridProps();
    }

    if (e.key === " " && state.ui.activeTab === "timeline") {
      e.preventDefault();
      togglePlayback();
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      closeModal();
      const dd = document.getElementById("timeline-add-dropdown");
      if (dd) dd.classList.remove("open");
    }
  });

  document.getElementById("modal-overlay").addEventListener("click", (e) => {
    if (e.target === e.currentTarget) closeModal();
  });
}

// ================================================================
// BOOT
// ================================================================
renderNav();
renderAuthButton();
// Must await before startRouter: Supabase parses auth tokens from the URL
// hash during init, and the hash router would otherwise stomp them by
// redirecting away from `#access_token=...` before detection completes.
(async () => {
  await initAuth();
  startRouter();
})();
